// The loaded file, plus derived lookups the UI needs: gap-filled top-level
// segments, a byte↔screen layout, byte↔time mapping, and cached lazy children.
import type { Explanation, Field, ParsedFile, Segment, SegmentKind } from './parsers/types';
import type { CodecAnalysis } from './codecs/types';

const childCache = new WeakMap<Segment, Segment[]>();
const fieldCache = new WeakMap<Segment, Field[]>();

/** Children of a segment. Lazy children are computed once and cached, so identity is stable. */
export function kids(s: Segment): Segment[] {
  if (!s.children) return [];
  if (Array.isArray(s.children)) return s.children;
  let c = childCache.get(s);
  if (!c) {
    try {
      c = s.children();
    } catch (e) {
      console.warn('children() failed for', s.name, e);
      c = [];
    }
    childCache.set(s, c);
  }
  return c;
}

export function fieldsOf(s: Segment): Field[] {
  if (!s.fields) return [];
  if (Array.isArray(s.fields)) return s.fields;
  let f = fieldCache.get(s);
  if (!f) {
    try {
      f = s.fields();
    } catch (e) {
      console.warn('fields() failed for', s.name, e);
      f = [];
    }
    fieldCache.set(s, f);
  }
  return f;
}

export const hasKids = (s: Segment) => (Array.isArray(s.children) ? s.children.length > 0 : !!s.children);

/** Time span covered by a segment: its own time, or the union of the frames inside it. */
export function timeOf(m: Model, s: Segment): [number, number] | null {
  if (s.time) return s.time;
  const i0 = m.frameIndexAtByte(s.start, true);
  if (i0 < 0) return null;
  let i1 = i0;
  while (i1 + 1 < m.frames.length && m.frames[i1 + 1].start < s.end) i1++;
  const f0 = m.frames[i0];
  if (f0.start >= s.end || f0.end <= s.start) return null;
  return [f0.time![0], m.frames[i1].time![1]];
}

/**
 * Piecewise-linear map between byte offsets and layout units in [0, 1].
 * Tiny non-audio blocks get a minimum width so a 34-byte STREAMINFO is still
 * visible next to 30 MB of audio. Those blocks are flagged as "not to scale".
 */
export class ByteLayout {
  readonly bytes: number[] = [];
  readonly units: number[] = [];
  readonly inflated = new Set<Segment>();
  constructor(top: Segment[], size: number) {
    const minFrac = 0.006;
    const weights = top.map((s) => s.end - s.start);
    const smallIdx = top.map((s, i) => (s.kind !== 'audio' && weights[i] < minFrac * size ? i : -1)).filter((i) => i >= 0);
    // Cap total inflation at ~25% of the bar so audio stays visually dominant.
    const minW = Math.min(minFrac * size, (0.25 * size) / Math.max(1, smallIdx.length));
    for (const i of smallIdx) {
      if (weights[i] < minW) {
        weights[i] = minW;
        this.inflated.add(top[i]);
      }
    }
    const total = weights.reduce((a, b) => a + b, 0) || 1;
    let u = 0;
    this.bytes.push(0);
    this.units.push(0);
    top.forEach((s, i) => {
      u += weights[i] / total;
      this.bytes.push(s.end);
      this.units.push(u);
    });
    if (this.bytes.length === 1) {
      this.bytes.push(size);
      this.units.push(1);
    }
  }
  private seg(arr: number[], v: number): number {
    let lo = 0, hi = arr.length - 2;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (arr[mid] <= v) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }
  toU(byte: number): number {
    const i = this.seg(this.bytes, byte);
    const b0 = this.bytes[i], b1 = this.bytes[i + 1];
    const f = b1 > b0 ? (byte - b0) / (b1 - b0) : 0;
    return this.units[i] + f * (this.units[i + 1] - this.units[i]);
  }
  toByte(u: number): number {
    const i = this.seg(this.units, u);
    const u0 = this.units[i], u1 = this.units[i + 1];
    const f = u1 > u0 ? (u - u0) / (u1 - u0) : 0;
    return this.bytes[i] + f * (this.bytes[i + 1] - this.bytes[i]);
  }
}

export class Model {
  readonly top: Segment[];
  readonly frames: Segment[];
  readonly layout: ByteLayout;
  readonly duration: number;
  /** Codec-level analysis (MP3/AAC/FLAC/Opus/Vorbis), when one applies. */
  codec: CodecAnalysis | null = null;
  private frameIdx: Map<Segment, number> | null = null;
  private readonly fStart: Float64Array;
  private readonly fT0: Float64Array;

  constructor(
    readonly pf: ParsedFile,
    readonly bytes: Uint8Array,
    readonly name: string,
    readonly explanations: Record<string, Explanation>,
    readonly parseError?: string,
  ) {
    this.top = fillGaps(pf.segments, pf.size, bytes);
    this.frames = pf.frames.filter((f) => f.time);
    this.layout = new ByteLayout(this.top, pf.size);
    this.fStart = Float64Array.from(this.frames, (f) => f.start);
    this.fT0 = Float64Array.from(this.frames, (f) => f.time![0]);
    const lastT = this.frames.length ? this.frames[this.frames.length - 1].time![1] : 0;
    this.duration = Math.max(lastT, pf.audio.duration ?? 0);
  }

  /** Index of the frame containing `byte`, or -1. With `atOrAfter`, the first frame ending after `byte`. */
  frameIndexAtByte(byte: number, atOrAfter = false): number {
    const a = this.fStart;
    let lo = 0, hi = a.length - 1, ans = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (a[mid] <= byte) { ans = mid; lo = mid + 1; } else hi = mid - 1;
    }
    if (ans >= 0 && this.frames[ans].end > byte) return ans;
    if (atOrAfter) return ans + 1 < a.length ? ans + 1 : -1;
    return -1;
  }

  frameIndexAtTime(t: number): number {
    const a = this.fT0;
    let lo = 0, hi = a.length - 1, ans = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (a[mid] <= t) { ans = mid; lo = mid + 1; } else hi = mid - 1;
    }
    return ans;
  }

  timeAtByte(byte: number): number | null {
    const i = this.frameIndexAtByte(byte);
    if (i < 0) return null;
    const f = this.frames[i];
    const r = (byte - f.start) / Math.max(1, f.end - f.start);
    return f.time![0] + r * (f.time![1] - f.time![0]);
  }

  byteAtTime(t: number): number | null {
    const i = this.frameIndexAtTime(t);
    if (i < 0) return null;
    const f = this.frames[i];
    const d = f.time![1] - f.time![0];
    const r = d > 0 ? Math.min(1, (t - f.time![0]) / d) : 0;
    return f.start + r * (f.end - f.start);
  }

  /** Byte span of the frames that play during [t0, t1]. */
  bytesForTime(t0: number, t1: number): [number, number] | null {
    let i0 = Math.max(0, this.frameIndexAtTime(t0));
    let i1 = Math.max(0, this.frameIndexAtTime(t1));
    if (!this.frames.length) return null;
    const b0 = this.byteAtTime(t0) ?? this.frames[i0].start;
    const b1 = this.byteAtTime(t1) ?? this.frames[i1].end;
    return [Math.min(b0, b1), Math.max(b0, b1)];
  }

  /** Index in `frames` of this segment, or of the frame that contains it; -1 if none. */
  frameIndexOf(s: Segment): number {
    if (!this.frameIdx) this.frameIdx = new Map(this.frames.map((f, i) => [f, i]));
    const direct = this.frameIdx.get(s);
    if (direct !== undefined) return direct;
    const i = this.frameIndexAtByte(s.start);
    if (i >= 0 && this.frames[i].start <= s.start && s.end <= this.frames[i].end) return i;
    return -1;
  }

  /** Path from a top-level segment down to `target` (inclusive), or null. */
  pathTo(target: Segment): Segment[] | null {
    const walk = (list: Segment[], acc: Segment[]): Segment[] | null => {
      for (const s of list) {
        if (s === target) return [...acc, s];
        if (s.start <= target.start && target.end <= s.end && hasKids(s)) {
          // Avoid resolving lazy children of big groups needlessly: only descend when containing.
          const r = walk(kids(s), [...acc, s]);
          if (r) return r;
        }
      }
      return null;
    };
    return walk(this.top, []);
  }

  /** Deepest segment containing `byte` whose on-screen width is at least `minPx`. */
  segmentAt(byte: number, pxPerByte: number, minPx = 3): Segment[] {
    const path: Segment[] = [];
    let list = this.top;
    for (;;) {
      const s = findContaining(list, byte);
      if (!s) break;
      if (path.length && (s.end - s.start) * pxPerByte < minPx) break;
      path.push(s);
      if (!hasKids(s)) break;
      // Only descend into lazy children when the parent is wide enough to show them.
      if (!Array.isArray(s.children) && (s.end - s.start) * pxPerByte < 40) break;
      list = kids(s);
    }
    return path;
  }

  explain(type: string): Explanation | undefined {
    if (this.explanations[type]) return this.explanations[type];
    // Fall back to a less specific key: "id3v2.frame.TIT2" → "id3v2.frame".
    let t = type;
    while (t.includes('.')) {
      t = t.slice(0, t.lastIndexOf('.'));
      if (this.explanations[t]) return this.explanations[t];
    }
    return undefined;
  }
}

function findContaining(list: Segment[], byte: number): Segment | undefined {
  let lo = 0, hi = list.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const s = list[mid];
    if (byte < s.start) hi = mid - 1;
    else if (byte >= s.end) lo = mid + 1;
    else return s;
  }
  return undefined;
}

/** Ensures the top level covers every byte, marking unclaimed spans as unknown (or zero padding). */
export function fillGaps(segs: Segment[], size: number, bytes: Uint8Array): Segment[] {
  const sorted = [...segs].sort((a, b) => a.start - b.start);
  const out: Segment[] = [];
  let pos = 0;
  const gap = (a: number, b: number) => {
    let zeros = true;
    for (let i = a; i < b && zeros; i++) if (bytes[i] !== 0) zeros = false;
    out.push({
      kind: zeros ? 'padding' : 'unknown', type: zeros ? 'gap.zeros' : 'gap.unknown',
      name: zeros ? 'Zero bytes' : 'Unrecognized bytes', start: a, end: b,
    });
  };
  for (const s of sorted) {
    if (s.start > pos) gap(pos, s.start);
    if (s.end <= pos && s.start < pos) continue; // fully overlapped; skip
    out.push(s);
    pos = Math.max(pos, s.end);
  }
  if (pos < size) gap(pos, size);
  return out;
}

/** Byte totals per kind, counting leaf-ish content: a container's own bytes count as its children's kinds. */
export function composition(m: Model): Map<SegmentKind, number> {
  const tot = new Map<SegmentKind, number>();
  const add = (k: SegmentKind, n: number) => tot.set(k, (tot.get(k) ?? 0) + n);
  const walk = (s: Segment, depth: number) => {
    // Only look inside containers; frames, tags etc. count as a whole.
    if (s.kind === 'container' && depth < 6 && Array.isArray(s.children) && s.children.length) {
      let covered = 0;
      for (const c of s.children) { walk(c, depth + 1); covered += c.end - c.start; }
      add('header', Math.max(0, s.end - s.start - covered));
    } else if (s.kind === 'container' && depth < 6 && typeof s.children === 'function') {
      const c = kids(s);
      let covered = 0;
      for (const x of c) { walk(x, depth + 1); covered += x.end - x.start; }
      add('header', Math.max(0, s.end - s.start - covered));
    } else add(s.kind, s.end - s.start);
  };
  for (const s of m.top) walk(s, 0);
  return tot;
}
